/**
 * Account > People & roles (approved mockup 2026-10-23). One company at a time:
 * Vault roles and Admin always show; one add-on's roles show next to them, picked with
 * "Add-on roles" (approved 2026-10-29, so the table fits a laptop with four parts), and
 * "Other add-ons" lists the roles someone has in the rest. Sign out, Remove and Reset
 * two-step are under Details. "All companies" keeps the one-table view of
 * everyone across the companies you administer (ADR-0015).
 * The "Authors can't approve their own work" switch lives in Vault > Reviews & approvals.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { api, type AdminTenant, type Member, type Person, type Role, type WfPerson } from "../lib/api";
import { useStore } from "../lib/store";
import { byCoOrder, coName } from "../lib/brand";
import { fmt } from "../lib/format";
import { Avatar, Loading, SaveBar, Tile } from "../components/bits";
import { Icon } from "../components/Icon";
import { PartTag } from "./settings/SettingsLayout";
import { CompanyPicker } from "./settings/CompanyPicker";

const VAULT_ROLES: Role[] = ["author", "reviewer", "approver"];
const CH_ROLES: Role[] = ["clienthub_viewer", "clienthub_editor", "clienthub_accounting"];
const RH_ROLES: Role[] = ["reviewhub_viewer", "reviewhub_editor"];
const VH_ROLES: Role[] = ["vendorhub_viewer", "vendorhub_editor"];
const ROLE_ORDER: Role[] = ["author", "reviewer", "approver", "admin"];
const ROLE_SHORT: Record<string, string> = { author: "Author", reviewer: "Reviewer", approver: "Approver", admin: "Admin", clienthub_viewer: "TechHub viewer", clienthub_editor: "TechHub editor", clienthub_accounting: "Accounting", reviewhub_viewer: "ReviewHub viewer", reviewhub_editor: "ReviewHub editor", vendorhub_viewer: "VendorHub viewer", vendorhub_editor: "VendorHub editor" };

type Addon = "clienthub" | "reviewhub" | "vendorhub";
const ADDON: Record<Addon, { label: string; roles: Role[]; cls: "c" | "r" | "h"; grp: string }> = {
  clienthub: { label: "TechHub", roles: CH_ROLES, cls: "c", grp: "gc" }, reviewhub: { label: "ReviewHub", roles: RH_ROLES, cls: "r", grp: "gr" }, vendorhub: { label: "VendorHub", roles: VH_ROLES, cls: "h", grp: "gh" },
};
const ROLE_HELP: Partial<Record<Role, string>> = {
  clienthub_accounting: "Sees and changes agreement dollar values and opens signed copies; nothing else",
  reviewhub_editor: "Builds reviews: ratings, risks, roadmap, commitments, the client page",
  vendorhub_viewer: "Sees vendors and opens their evidence", vendorhub_editor: "Adds and changes vendors, evidence and findings; records reviews",
};
const COL: Partial<Record<Role, string>> = { clienthub_viewer: "Viewer", clienthub_editor: "Editor", clienthub_accounting: "Accounting", reviewhub_viewer: "Viewer", reviewhub_editor: "Editor", vendorhub_viewer: "Viewer", vendorhub_editor: "Editor" };
const ADDON_KEY = "dv.team.addon";
const readAddon = () => { try { return localStorage.getItem(ADDON_KEY) as Addon | null; } catch { return null; } };

const same = (a: Role[], b: Role[]) => a.length === b.length && a.every((r) => b.includes(r));

/** Unsaved role changes: Save / Undo bar, and a browser warning if you leave with changes. */

export function MembersPage() {
  const { p } = useStore();
  const [sp, setSp] = useSearchParams();
  const view = sp.get("view") === "all" ? "all" : "company";
  if (!p.anyAdmin) return <Navigate to="/settings" replace />;
  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="account" />
        <h1 className="title">Team</h1>
        <div className="lede">One list of people. Roles are grouped by part, so it's clear what someone can do in the Vault and in each add-on (TechHub, ReviewHub, VendorHub).</div>
      </div></div>
      <div className="tabs" role="tablist">
        <button className={`tab ${view === "company" ? "on" : ""}`} role="tab" aria-selected={view === "company"} onClick={() => setSp({}, { replace: true })}>By company</button>
        <button className={`tab ${view === "all" ? "on" : ""}`} role="tab" aria-selected={view === "all"} onClick={() => setSp({ view: "all" }, { replace: true })}>All companies</button>
      </div>
      {view === "company" ? <ByCompany /> : <PeopleTable />}
    </div>
  );
}

function ByCompany() {
  const { me, ws, toast, reload } = useStore();
  const [sp, setSp] = useSearchParams();
  const [tenants, setTenants] = useState<AdminTenant[] | null>(null);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, Role[]>>({});
  const [saving, setSaving] = useState(false);
  const [people, setPeople] = useState<Map<string, WfPerson>>(new Map());
  const [details, setDetails] = useState<string | null>(null);
  const [addonPick, setAddonPick] = useState<Addon | null>(readAddon);

  const codes = (tenants ?? []).map((t) => t.code);
  const wanted = sp.get("co") ?? (ws !== "ALL" ? ws : "");
  const c = codes.includes(wanted) ? wanted : codes[0] ?? "";
  const t = tenants?.find((x) => x.code === c);

  const loadTenants = async () => { try { setTenants((await api.adminTenants()).tenants.sort(byCoOrder)); } catch (e) { setErr((e as Error).message); } };
  const loadMembers = async (code: string) => { try { setMembers((await api.members(code)).members); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void loadTenants(); }, []);
  const loadPeople = async (code: string) => { try { setPeople(new Map((await api.wfPeople(code)).people.map((x) => [x.user_id, x]))); } catch { setPeople(new Map()); } };
  useEffect(() => { if (c) { setMembers(null); setDetails(null); void loadMembers(c); void loadPeople(c); } }, [c]);

  if (err) return <div className="card empty"><h3>People didn't load</h3>{err}</div>;
  if (!tenants || !t) return <Loading />;
  const hub = (t.modules ?? []).includes("clienthub");
  const rhub = hub && (t.modules ?? []).includes("reviewhub");
  const vhub = (t.modules ?? []).includes("vendorhub");
  const addons = ([hub && "clienthub", rhub && "reviewhub", vhub && "vendorhub"].filter(Boolean)) as Addon[];
  const addon: Addon | null = addonPick && addons.includes(addonPick) ? addonPick : addons[0] ?? null;
  const others = addons.filter((a) => a !== addon);
  const pickAddon = (a: Addon) => { setAddonPick(a); try { localStorage.setItem(ADDON_KEY, a); } catch { /* per-viewer convenience only */ } };
  const rolesOf = (m: Member) => draft[m.id] ?? m.roles;
  const changes = (members ?? []).filter((m) => draft[m.id]);
  const nChanges = changes.reduce((n, m) => n + [...new Set([...m.roles, ...draft[m.id]!])].filter((r) => m.roles.includes(r) !== draft[m.id]!.includes(r)).length, 0);

  const refresh = async () => { await Promise.all([loadTenants(), loadMembers(c), loadPeople(c)]); };
  // Ticking a box only stages the change; Save applies it (Bill, 2026-10-23: a stray click must not change access).
  const toggle = (m: Member, r: Role) => {
    const cur = rolesOf(m);
    const next = cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r];
    setDraft((d) => { const n = { ...d }; if (same(next, m.roles)) delete n[m.id]; else n[m.id] = next; return n; });
  };
  const save = async () => {
    if (!members) return;
    if (!members.some((m) => rolesOf(m).includes("approver"))) return toast("Every company needs at least one approver.");
    setSaving(true);
    let done = 0;
    try {
      for (const m of changes) {
        const roles = draft[m.id]!;
        await api.setRoles(c, { user_id: m.id, roles: roles.length ? roles : ["reader"] });
        done++;
      }
      toast(`Saved ${done} ${done === 1 ? "person's" : "people's"} roles in ${coName(c)}.`);
      setDraft({});
      await refresh();
      if (changes.some((m) => m.id === me.user.id)) await reload();
    } catch (e) { toast(`${(e as Error).message}${done ? ` (${done} saved before this)` : ""}`); await refresh(); setDraft({}); }
    finally { setSaving(false); }
  };
  const pick = (x: string) => {
    if (nChanges && !window.confirm(`You have ${nChanges} unsaved change${nChanges === 1 ? "" : "s"} in ${coName(c)}. Discard them?`)) return;
    setDraft({});
    setSp({ co: x }, { replace: true });
  };
  const signOutMember = async (m: Member) => {
    if (!window.confirm(`Sign ${m.display_name} out of DocVault on every device? They can sign in again straight away; use Remove to take their access away.`)) return;
    try { await api.signOutUser(m.id); toast(`${m.display_name} is signed out everywhere. Any open window stops working within the hour.`); }
    catch (e) { toast((e as Error).message); }
  };
  const resetTwoStep = async (m: Member) => {
    if (!window.confirm(`Reset two-step sign-in for ${m.display_name}? Their authenticator stops working, they're signed out everywhere, and they set it up again at their next sign-in. Use this for a lost or replaced phone.`)) return;
    try { await api.mfaReset(m.id); toast(`Two-step sign-in reset for ${m.display_name}. They'll set it up again at their next sign-in, and they've been emailed.`); await refresh(); }
    catch (e) { toast((e as Error).message); }
  };
  const removeMember = async (m: Member) => {
    if (!window.confirm(`Remove ${m.display_name} from ${coName(c)}? They lose access to its documents${hub ? " and TechHub" : ""}.`)) return;
    try { await api.setRoles(c, { user_id: m.id, roles: [] }); toast(`${m.display_name} removed from ${coName(c)}.`); await refresh(); }
    catch (e) { toast((e as Error).message); }
  };
  const box = (m: Member, r: Role, part: "v" | "c" | "r" | "h" | "a") => {
    const on = rolesOf(m).includes(r);
    const changed = on !== m.roles.includes(r);
    const locked = r === "admin" && m.id === me.user.id && m.roles.includes("admin") && !me.user.is_platform_admin;
    return (
      <button className={`chkr ${part} ${on ? "on" : ""} ${changed ? "chg" : ""}`} disabled={locked || saving}
        aria-pressed={on} aria-label={`${ROLE_SHORT[r]}: ${m.display_name}`} title={locked ? "You can't remove your own admin role" : ""} onClick={() => void toggle(m, r)}>
        {on ? "✓" : ""}
      </button>
    );
  };

  return (
    <>
      <CompanyPicker tenants={tenants} code={c} onPick={pick} sub={(x) => `${x.members} member${x.members === 1 ? "" : "s"} · ${x.approvers} approver${x.approvers === 1 ? "" : "s"}`} />
      <SaveBar n={nChanges} busy={saving} onSave={() => void save()} onUndo={() => setDraft({})} />
      {addons.length > 1 && (
        <div className="tm-pick" role="group" aria-label="Add-on roles">
          <span>Add-on roles</span>
          {addons.map((a) => <button key={a} className={`tm-seg tm-${a} ${a === addon ? "on" : ""}`} aria-pressed={a === addon} onClick={() => pickAddon(a)}>{ADDON[a].label}</button>)}
        </div>
      )}
      <div className="card tbl-wrap ptbl-wrap">
        {!members ? <Loading /> : (
          <table className="tbl ptbl">
            <thead>
              <tr>
                <th rowSpan={2}>Person</th>
                <th colSpan={4} className="grp gv">Vault</th>
                {addon && <th colSpan={ADDON[addon].roles.length} className={`grp ${ADDON[addon].grp} sep`}>{ADDON[addon].label}</th>}
                {others.length > 0 && <th rowSpan={2} className="sep">Other add-ons</th>}
                <th rowSpan={2} className="c sep">Admin</th>
                <th rowSpan={2} className="sep"><span className="sr">Actions</span></th>
              </tr>
              <tr>
                <th className="c">Reader</th><th className="c">Author</th><th className="c">Reviewer</th><th className="c">Approver</th>
                {addon && ADDON[addon].roles.map((r, i) => <th key={r} className={`c${i === 0 ? " sep" : ""}`} title={ROLE_HELP[r]}>{COL[r]}</th>)}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => [
                <tr key={m.id}>
                  <td>
                    <span style={{ display: "inline-flex", gap: 10, alignItems: "center", minWidth: 0 }}>
                      <Avatar id={m.id} name={m.display_name} size={30} />
                      <span style={{ minWidth: 0 }}>
                        <b style={{ color: "var(--ink)", fontWeight: 600 }}>{m.display_name}</b>{m.id === me.user.id && <span style={{ color: "var(--muted)" }}> (you)</span>}<br />
                        <span style={{ fontSize: 12, color: "var(--muted)" }}>{m.title ? `${m.title} · ` : ""}{m.email}</span>
                        {(() => { const pp = people.get(m.id); const bits = [pp?.manager_name && `Manager: ${pp.manager_name}`, pp?.starts_on && `Starts ${fmt(pp.starts_on)}`, pp?.leaving_on && `Last day ${fmt(pp.leaving_on)}`].filter(Boolean);
                          return bits.length ? <><br /><span style={{ fontSize: 12, color: "var(--muted)" }}>{bits.join(" · ")}</span></> : null; })()}
                        {!m.last_login_at && <><br /><span className="pill sm draft">Invited · not signed in yet</span></>}
                        {m.last_login_at && m.mfa && <><br /><span className={`pill sm ${m.mfa === "on" ? "pub" : m.mfa === "microsoft" ? "info" : "warn"}`} title={m.mfa === "on" ? "Two-step sign-in is on" : m.mfa === "microsoft" ? "Signs in with Microsoft, which does the second step" : "Hasn't set up two-step sign-in yet; asked at next sign-in"}>{m.mfa === "on" ? "Two-step on" : m.mfa === "microsoft" ? "Microsoft" : "No two-step"}</span></>}
                      </span>
                    </span>
                  </td>
                  <td className="c"><span className="chkr v on fixed" title="Every member can read the company's approved documents">✓</span></td>
                  {VAULT_ROLES.map((r) => <td key={r} className="c">{box(m, r, "v")}</td>)}
                  {addon && ADDON[addon].roles.map((r, i) => <td key={r} className={`c${i === 0 ? " sep" : ""}`}>{box(m, r, ADDON[addon].cls)}</td>)}
                  {others.length > 0 && <td className="sep">
                    <div className="tm-other">{others.flatMap((a) => ADDON[a].roles.filter((r) => rolesOf(m).includes(r)).map((r) =>
                      <button key={r} className={`tm-chip tm-${a} ${rolesOf(m).includes(r) !== m.roles.includes(r) ? "chg" : ""}`} title={`Show ${ADDON[a].label} roles`} onClick={() => pickAddon(a)}>{ROLE_SHORT[r]}</button>))}</div>
                  </td>}
                  <td className="c sep">{box(m, "admin", "a")}</td>
                  <td className="sep acts">
                    <button className="btn btn-sm btn-ghost" onClick={() => setDetails(details === m.id ? null : m.id)} title="Manager, dates, sign out, remove">{details === m.id ? "Close" : "Details"}</button>
                  </td>
                </tr>,
                details === m.id ? <tr key={`${m.id}-d`}><td colSpan={99}>
                  {m.id !== me.user.id && <div className="row-btns tm-acts">
                    <button className="btn btn-sm btn-ghost" disabled={saving} onClick={() => void signOutMember(m)} title="End their sessions on every device (lost laptop)">Sign out everywhere</button>
                    {m.mfa === "on" && <button className="btn btn-sm btn-ghost" disabled={saving} onClick={() => void resetTwoStep(m)} title="Lost or replaced phone">Reset two-step</button>}
                    <button className="btn btn-sm btn-ghost" disabled={saving || !!draft[m.id]} onClick={() => void removeMember(m)} title="Remove from this company">Remove from {coName(c)}</button>
                  </div>}
                  <PersonDetails company={c} m={m} p={people.get(m.id)} members={members} onSaved={async (msg) => { toast(msg); setDetails(null); await refresh(); }} /></td></tr> : null,
              ])}
            </tbody>
          </table>
        )}
        <AddMember company={c} existing={new Set((members ?? []).map((m) => m.id))} onAdded={refresh} />
      </div>
      <div className="foot-note">
        Tick or untick roles, then Save changes; nothing applies until you save. Every member is a Reader of {coName(c)}'s approved documents; Remove takes that away (it asks first). {hub ? "TechHub viewers see clients and add notes; editors also change client records and open signed agreements; Accounting sees and changes agreement dollar values and opens signed agreements, and nothing else. Admins have everything, including TechHub's admin-only details. " : "TechHub columns appear when TechHub is on for this company. "}{rhub ? "ReviewHub viewers see the quarterly reviews; editors build them. Company admins are ReviewHub admins. " : ""}{vhub ? "VendorHub viewers see vendors and open their evidence; editors add and change them. Company admins are VendorHub admins. " : ""}
        Two-step sign-in is required for everyone who signs in with a password; people who haven't set it up are asked at their next sign-in. Reset two-step is for a lost phone: they set it up again at their next sign-in, and it's in the audit log.{" "}
        Who approves what is set in <Link className="doclink" to={`/settings/approvals?co=${c}`}>Reviews &amp; approvals</Link>.
      </div>
    </>
  );
}

// ---------------------------------------------------------------- all companies (ADR-0015)
function PeopleTable() {
  const { toast, me, reload } = useStore();
  const [data, setData] = useState<{ companies: { code: string; name: string }[]; people: Person[]; unassigned: number } | null>(null);
  const [hub, setHub] = useState<Set<string>>(new Set());
  const [rhub, setRhub] = useState<Set<string>>(new Set());
  const [vhub, setVhub] = useState<Set<string>>(new Set());
  useEffect(() => { api.adminTenants().then((r) => {
    setHub(new Set(r.tenants.filter((x) => (x.modules ?? []).includes("clienthub")).map((x) => x.code)));
    setRhub(new Set(r.tenants.filter((x) => (x.modules ?? []).includes("clienthub") && (x.modules ?? []).includes("reviewhub")).map((x) => x.code)));
    setVhub(new Set(r.tenants.filter((x) => (x.modules ?? []).includes("vendorhub")).map((x) => x.code)));
  }, () => undefined); }, []);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState<Record<string, Role[]>>({}); // key: person id | company code
  const [saving, setSaving] = useState(false);
  const load = async () => { try { setData(await api.people()); } catch (e) { setErr((e as Error).message); } };
  useEffect(() => { void load(); }, []);
  const companies = useMemo(() => [...(data?.companies ?? [])].sort(byCoOrder), [data]);
  if (err) return <div className="note bad">{err}</div>;
  if (!data) return <Loading />;

  const list = data.people.filter((p) => !q || `${p.name} ${p.email} ${p.title ?? ""}`.toLowerCase().includes(q.toLowerCase()));
  const key = (person: Person, code: string) => `${person.id}|${code}`;
  const rolesOf = (person: Person, code: string) => draft[key(person, code)] ?? person.roles[code] ?? [];
  const stage = (person: Person, code: string, next: Role[]) => {
    const k = key(person, code);
    setDraft((d) => { const n = { ...d }; if (same(next, person.roles[code] ?? [])) delete n[k]; else n[k] = next; return n; });
  };
  const toggle = (person: Person, code: string, r: Role) => {
    const cur = rolesOf(person, code);
    stage(person, code, cur.includes(r) ? cur.filter((x) => x !== r) : [...new Set([...cur, "reader" as Role, r])]);
  };
  const access = (person: Person, code: string) => stage(person, code, rolesOf(person, code).length ? [] : ["reader"]);
  const nChanges = Object.keys(draft).length;
  const save = async () => {
    const byKey = new Map(data.people.map((x) => [x.id, x]));
    const items = Object.entries(draft).map(([k, roles]) => { const [id, code] = k.split("|") as [string, string]; return { person: byKey.get(id)!, code, roles }; });
    for (const code of new Set(items.map((i) => i.code))) {
      if (!data.people.some((x) => rolesOf(x, code).includes("approver"))) return toast(`${coName(code)} needs at least one approver.`);
    }
    const removals = items.filter((i) => !i.roles.length);
    if (removals.length && !window.confirm(`This removes ${removals.map((i) => `${i.person.name} from ${coName(i.code)}`).join(", ")}. They lose access to its documents. Save anyway?`)) return;
    setSaving(true);
    let done = 0;
    try {
      for (const i of items) { await api.setRoles(i.code, { user_id: i.person.id, roles: i.roles }); done++; }
      toast(`Saved ${done} change${done === 1 ? "" : "s"}.`);
      setDraft({}); await load();
      if (items.some((i) => i.person.id === me.user.id)) await reload();
    } catch (e) { toast(`${(e as Error).message}${done ? ` (${done} saved before this)` : ""}`); setDraft({}); await load(); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="filters">
        <label className="inline-search"><Icon name="search" size={16} />
          <input placeholder="Find a person" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a person" />
        </label>
        <span className="cap">{list.length} of {data.people.length} people{data.unassigned ? ` · ${data.unassigned} signed in but not in any company yet` : ""}</span>
      </div>
      <SaveBar n={nChanges} busy={saving} onSave={() => void save()} onUndo={() => setDraft({})} />
      <div className="card tbl-wrap">
        <table className="tbl ppl-tbl">
          <colgroup><col style={{ width: "28%" }} />{companies.map((c) => <col key={c.code} />)}</colgroup>
          <thead><tr><th>Person</th>{companies.map((c) => <th key={c.code}><span style={{ display: "inline-flex", gap: 6, alignItems: "center", textTransform: "none", letterSpacing: 0 }}><Tile code={c.code} />{coName(c.code, c.name)}</span></th>)}</tr></thead>
          <tbody>
            {list.map((person) => (
              <tr key={person.id}>
                <td>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <Avatar id={person.id} name={person.name} size={28} />
                    <div className="who"><b>{person.name}</b>{!person.active && <span className="pill draft" style={{ marginLeft: 6 }}>Inactive</span>}
                      <small>{person.email}{person.last_login_at ? ` · last in ${fmt(person.last_login_at)}` : " · never signed in"}</small></div>
                  </div>
                </td>
                {companies.map((c) => {
                  const roles = rolesOf(person, c.code);
                  const saved = person.roles[c.code] ?? [];
                  const member = roles.length > 0;
                  const b = saving;
                  return (
                    <td key={c.code}>
                      <div className="rolecell">
                        <button className={`rchip ${member ? "on" : ""} ${member !== saved.length > 0 ? "chg" : ""}`} disabled={b} title={member ? "Remove from this company" : "Give read access"} onClick={() => access(person, c.code)}>{member ? "Member" : "+ Add"}</button>
                        {member && [...ROLE_ORDER, ...(hub.has(c.code) ? CH_ROLES : []), ...(rhub.has(c.code) ? RH_ROLES : []), ...(vhub.has(c.code) ? VH_ROLES : [])].map((r) => (
                          <button key={r} className={`rchip ${roles.includes(r) ? "on" : ""} ${roles.includes(r) !== saved.includes(r) ? "chg" : ""}`} disabled={b} aria-pressed={roles.includes(r)} onClick={() => toggle(person, c.code, r)}>{ROLE_SHORT[r]}</button>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="foot-note">Click to change roles, then Save changes; nothing applies until you save. Every member can read their company's approved documents. To add someone new, use "Add / invite" under By company. Every change is in the audit log.</div>
    </>
  );
}

function AddMember({ company, existing, onAdded }: { company: string; existing: Set<string>; onAdded: () => Promise<void> }) {
  const { toast } = useStore();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<{ id: string; email: string; display_name: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || email.trim().length < 2) { setUsers([]); return; }
    const t = setTimeout(() => { api.users(email.trim()).then((r) => setUsers(r.users), () => setUsers([])); }, 200);
    return () => clearTimeout(t);
  }, [email, open]);
  const list = users.filter((u) => !existing.has(u.id)).slice(0, 6);
  const toggle = (r: Role) => setRoles((rs) => (rs.includes(r) ? rs.filter((x) => x !== r) : [...rs, r]));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return toast("Enter their work email address.");
    setBusy(true);
    try {
      const r = await api.invite(company, { email: email.trim(), name: name.trim() || undefined, roles });
      toast(r.created ? `Invitation sent to ${email.trim()}. They're in ${coName(company)} as soon as they sign in.` : `${email.trim()} added to ${coName(company)}.`);
      setEmail(""); setName(""); setRoles([]);
      await onAdded();
    } catch (err) { toast((err as Error).message); }
    finally { setBusy(false); }
  };
  return (
    <form className="member-add" onSubmit={submit}>
      <div style={{ position: "relative", flex: "2 1 260px" }}>
        <span className="lbl">Add or invite someone to {coName(company)}</span>
        <input className="inp" type="email" placeholder="Work email, e.g. jane@itassurancemsp.com" value={email} autoComplete="off"
          onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 200)} onChange={(e) => { setEmail(e.target.value); setOpen(true); }} />
        {open && list.length > 0 && (
          <div className="suggest">
            {list.map((u) => (
              <button type="button" key={u.id} onMouseDown={(e) => e.preventDefault()} onClick={() => { setEmail(u.email); setName(u.display_name); setOpen(false); }}>
                {u.display_name}<small>{u.email} · already uses DocVault</small>
              </button>
            ))}
          </div>
        )}
      </div>
      <label style={{ flex: "1 1 180px" }}><span className="lbl">Name <span style={{ fontWeight: 400, color: "var(--muted)" }}>(new people)</span></span>
        <input className="inp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Smith" maxLength={120} /></label>
      <div>
        <span className="lbl">Also make them</span>
        <div style={{ display: "flex", gap: 4 }}>
          {(["author", "reviewer", "approver", "admin"] as Role[]).map((r) => (
            <button type="button" key={r} className={`btn btn-sm role-btn role-${r} ${roles.includes(r) ? "on" : ""}`} onClick={() => toggle(r)}>
              {roles.includes(r) ? "✓ " : "+ "}{roleLabel(r)}
            </button>
          ))}
        </div>
      </div>
      <button className={`btn btn-accent ${busy ? "busy" : ""}`} type="submit">Add / invite</button>
      <div className="help" style={{ flexBasis: "100%", margin: 0 }}>Everyone added can read {coName(company)} documents. New people get an email with a temporary password; people who already use DocVault just get access.</div>
    </form>
  );
}

/** ADR-0022: TechHub roles read as "TechHub viewer" / "TechHub editor". */
function roleLabel(r: string) {
  return r === "clienthub_viewer" ? "TechHub viewer" : r === "clienthub_editor" ? "TechHub editor" : r === "clienthub_accounting" ? "TechHub Accounting"
    : r === "reviewhub_viewer" ? "ReviewHub viewer" : r === "reviewhub_editor" ? "ReviewHub editor" : r === "vendorhub_viewer" ? "VendorHub viewer" : r === "vendorhub_editor" ? "VendorHub editor" : r.charAt(0).toUpperCase() + r.slice(1);
}

/** Manager, start date and last day (ADR-0025): a last day starts Employee offboarding. */
function PersonDetails({ company, m, p, members, onSaved }: { company: string; m: Member; p?: WfPerson; members: Member[]; onSaved: (msg: string) => Promise<void> }) {
  const { toast } = useStore();
  const [mgr, setMgr] = useState(p?.manager_user_id ?? "");
  const [starts, setStarts] = useState(p?.starts_on ?? "");
  const [leaving, setLeaving] = useState(p?.leaving_on ?? "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (leaving && !p?.leaving_on && !window.confirm(`Set ${m.display_name}'s last day to ${fmt(leaving)}? Employee offboarding starts now, so the steps are ready before then.`)) return;
    setBusy(true);
    try {
      const r = await api.wfSetPerson(m.id, { company, manager_user_id: mgr || null, starts_on: starts || null, leaving_on: leaving || null });
      await onSaved(r.started ? `Saved. Employee offboarding for ${m.display_name} has started.` : "Saved.");
    } catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  return (
    <div className="form-row" style={{ alignItems: "flex-end", padding: "6px 0" }}>
      <label className="grow"><span className="lbl">Manager</span><select className="inp" value={mgr} onChange={(e) => setMgr(e.target.value)}>
        <option value="">No manager</option>{members.filter((x) => x.id !== m.id).map((x) => <option key={x.id} value={x.id}>{x.display_name}</option>)}</select></label>
      <label><span className="lbl">Start date</span><input className="inp" type="date" value={starts} onChange={(e) => setStarts(e.target.value)} /></label>
      <label><span className="lbl">Last day</span><input className="inp" type="date" value={leaving} onChange={(e) => setLeaving(e.target.value)} /></label>
      <div className="row-btns" style={{ margin: 0 }}><button className="btn btn-accent btn-sm" disabled={busy} onClick={() => void save()}>Save</button></div>
      <div className="cap" style={{ flexBasis: "100%" }}>The manager gets the manager steps in this person's workflows and can start workflows on them. Setting a last day starts Employee offboarding.</div>
    </div>
  );
}
